import { FunctionComponent, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    MenuItem,
    Slider,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useGetFaceValidationMeasurementsQuery } from '@/api/v2/faceValidation';
import {
    DEFAULT_THRESHOLDS,
    Thresholds,
    evaluate,
    summarise,
} from '@/pageContents/faceValidationContent/thresholds';
import { PhotoReview } from '@/pageContents/faceValidationContent/PhotoReview';

const REASON_LABEL: Record<string, string> = {
    NO_FACE_DETECTED: 'Kein Gesicht erkannt',
    FACE_OCCLUDED: 'Gesicht verdeckt',
    LOW_IMAGE_QUALITY: 'Bildqualität zu niedrig',
};

type SliderRowProps = {
    label: string;
    help: string;
    value: number;
    min: number;
    max: number;
    step?: number;
    onChange: (value: number) => void;
};

const SliderRow: FunctionComponent<SliderRowProps> = ({
    label,
    help,
    value,
    min,
    max,
    step = 1,
    onChange,
}) => (
    <Box sx={{ mb: 3 }}>
        <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='baseline'
        >
            <Typography variant='body2' fontWeight={600}>
                {label}
            </Typography>
            <Typography
                variant='body2'
                sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
                {value}
            </Typography>
        </Stack>
        <Typography variant='caption' color='text.secondary'>
            {help}
        </Typography>
        <Slider
            value={value}
            min={min}
            max={max}
            step={step}
            onChange={(_, v) => onChange(v as number)}
            size='small'
        />
    </Box>
);

/**
 * #1870 calibration screen.
 *
 * Answers one question: what would a different set of thresholds have decided about the photos
 * customers really uploaded? Moving a slider recalculates the whole set at once, so the cost of a
 * stricter setting is visible before anyone is affected by it.
 *
 * The go-live gate is a rejection rate under 5 %. That number is only meaningful against a
 * representative set — a handful of deliberately awkward photos will always look worse.
 */
export const FaceValidationPageContent: FunctionComponent = () => {
    const [days, setDays] = useState(30);
    const [thresholds, setThresholds] =
        useState<Thresholds>(DEFAULT_THRESHOLDS);

    const { data: measurements = [], isLoading } =
        useGetFaceValidationMeasurementsQuery({ days });

    const current = useMemo(
        () => summarise(measurements, thresholds),
        [measurements, thresholds]
    );
    const baseline = useMemo(
        () => summarise(measurements, DEFAULT_THRESHOLDS),
        [measurements]
    );

    const set = (key: keyof Thresholds) => (value: number) =>
        setThresholds((prev) => ({ ...prev, [key]: value }));

    const changed =
        JSON.stringify(thresholds) !== JSON.stringify(DEFAULT_THRESHOLDS);

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant='h4' sx={{ mb: 1 }}>
                Foto-Kalibrierung
            </Typography>
            <Typography color='text.secondary' sx={{ mb: 3, maxWidth: '68ch' }}>
                Hier siehst du, wie sich die Schwellwerte der automatischen
                Foto-Prüfung auf echte Uploads auswirken. Verschiebe einen
                Regler, um zu sehen, wie viele Fotos mit der neuen Einstellung
                abgelehnt worden wären. Es wird nichts verändert, solange die
                Werte nicht übernommen werden.
            </Typography>

            <TextField
                select
                size='small'
                label='Zeitraum'
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                sx={{ mb: 3, minWidth: 200 }}
            >
                <MenuItem value={7}>Letzte 7 Tage</MenuItem>
                <MenuItem value={30}>Letzte 30 Tage</MenuItem>
                <MenuItem value={90}>Letzte 90 Tage</MenuItem>
                <MenuItem value={365}>Letztes Jahr</MenuItem>
            </TextField>

            {!isLoading && measurements.length === 0 && (
                <Alert severity='info' sx={{ mb: 3 }}>
                    Für diesen Zeitraum liegen noch keine Messwerte vor. Sie
                    entstehen, sobald im Checkout ein Foto geprüft wird – also
                    bei einer Bahn mit aktivierter Fotoprüfung, einem dafür
                    freigegebenen Produkt mit Fotopflicht und laufender Prüfung
                    (Mitlauf oder scharf). Geprüft wird beim Hochladen eines
                    neuen Fotos und beim Zuweisen einer bestehenden Keycard zu
                    einem Ticket.
                </Alert>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
                <Card sx={{ flex: 1, minWidth: 0 }}>
                    <CardContent>
                        <Typography variant='h6' sx={{ mb: 2 }}>
                            Schwellwerte
                        </Typography>

                        <SliderRow
                            label='Mindestschärfe'
                            help='Darunter gilt das Foto als zu unscharf.'
                            value={thresholds.minSharpness}
                            min={0}
                            max={100}
                            onChange={set('minSharpness')}
                        />
                        <SliderRow
                            label='Mindesthelligkeit'
                            help='Darunter gilt das Foto als zu dunkel.'
                            value={thresholds.minBrightness}
                            min={0}
                            max={100}
                            onChange={set('minBrightness')}
                        />
                        <SliderRow
                            label='Sicherheit „Gesicht verdeckt“'
                            help='Ab dieser Sicherheit wird eine erkannte Verdeckung als Ablehnung gewertet. Höher = nachsichtiger.'
                            value={thresholds.occlusionConfidence}
                            min={50}
                            max={100}
                            onChange={set('occlusionConfidence')}
                        />
                        <SliderRow
                            label='Sicherheit „Sonnenbrille“'
                            help='Wie oben, für erkannte Sonnenbrillen.'
                            value={thresholds.sunglassesConfidence}
                            min={50}
                            max={100}
                            onChange={set('sunglassesConfidence')}
                        />
                        <SliderRow
                            label='Alterstoleranz in Jahren'
                            help='Weicht das geschätzte Alter weiter ab, erscheint ein Hinweis. Blockiert nie.'
                            value={thresholds.ageToleranceYears}
                            min={0}
                            max={25}
                            onChange={set('ageToleranceYears')}
                        />

                        {changed && (
                            <Alert severity='warning' sx={{ mt: 1 }}>
                                Geänderte Werte sind eine Vorschau. Damit sie
                                wirksam werden, müssen sie in der Konfiguration
                                hinterlegt werden — sag Christian Bescheid.
                            </Alert>
                        )}
                    </CardContent>
                </Card>

                <Card sx={{ flex: 1, minWidth: 0 }}>
                    <CardContent>
                        <Typography variant='h6' sx={{ mb: 2 }}>
                            Auswirkung
                        </Typography>

                        <Stack
                            direction='row'
                            spacing={3}
                            sx={{ mb: 2 }}
                            flexWrap='wrap'
                            useFlexGap
                        >
                            <Box>
                                <Typography
                                    variant='h3'
                                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                                    color={
                                        current.falseRejectionRate === null
                                            ? 'text.disabled'
                                            : current.falseRejectionRate > 5
                                              ? 'error.main'
                                              : 'success.main'
                                    }
                                >
                                    {current.falseRejectionRate === null
                                        ? '\u2013'
                                        : `${current.falseRejectionRate.toFixed(1)}%`}
                                </Typography>
                                <Typography
                                    variant='caption'
                                    color='text.secondary'
                                >
                                    zu Unrecht abgelehnt
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant='h3'
                                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                                >
                                    {current.rejectionRate.toFixed(1)}%
                                </Typography>
                                <Typography
                                    variant='caption'
                                    color='text.secondary'
                                >
                                    abgelehnt insgesamt
                                    {changed &&
                                        ` (heute ${baseline.rejectionRate.toFixed(1)}%)`}
                                </Typography>
                            </Box>
                            <Box>
                                <Typography
                                    variant='h3'
                                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                                >
                                    {current.total}
                                </Typography>
                                <Typography
                                    variant='caption'
                                    color='text.secondary'
                                >
                                    Fotos, {current.labelled} bewertet
                                </Typography>
                            </Box>
                        </Stack>

                        {current.falseRejectionRate === null &&
                            current.total > 0 && (
                                <Alert severity='info' sx={{ mb: 2 }}>
                                    Die entscheidende Zahl ist, wie viele
                                    Ablehnungen zu Unrecht passieren \u2014 und
                                    dafür muss jemand die Fotos ansehen. Bewerte
                                    unten ein paar Aufnahmen, dann erscheint sie
                                    hier.
                                </Alert>
                            )}

                        {current.falseRejectionRate !== null &&
                            current.falseRejectionRate > 5 && (
                                <Alert severity='error' sx={{ mb: 2 }}>
                                    Über 5 % der als in Ordnung bewerteten Fotos
                                    würden abgelehnt. Für eine Scharfschaltung
                                    sollte das darunter liegen.
                                </Alert>
                            )}

                        {current.falseAcceptances > 0 && (
                            <Alert severity='warning' sx={{ mb: 2 }}>
                                {current.falseAcceptances} als unbrauchbar
                                bewertete(s) Foto(s) würden durchgelassen.
                                Strengere Werte würden das ändern \u2014 auf
                                Kosten der Quote oben.
                            </Alert>
                        )}

                        <Typography variant='subtitle2' sx={{ mb: 1 }}>
                            Ablehnungsgründe
                        </Typography>
                        <Stack
                            direction='row'
                            spacing={1}
                            flexWrap='wrap'
                            useFlexGap
                        >
                            {Object.keys(REASON_LABEL).map((reason) => (
                                <Chip
                                    key={reason}
                                    size='small'
                                    label={`${REASON_LABEL[reason]}: ${current.byReason[reason] ?? 0}`}
                                    color={
                                        (current.byReason[reason] ?? 0) > 0
                                            ? 'default'
                                            : 'success'
                                    }
                                    variant='outlined'
                                />
                            ))}
                            <Chip
                                size='small'
                                variant='outlined'
                                label={`Altershinweise: ${current.ageWarnings}`}
                            />
                        </Stack>

                        <Typography variant='subtitle2' sx={{ mt: 3, mb: 1 }}>
                            Grenzfälle
                        </Typography>
                        <Typography variant='caption' color='text.secondary'>
                            Fotos, die mit den geänderten Werten anders bewertet
                            würden als heute — die sind es wert, angesehen zu
                            werden.
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            {measurements
                                .filter(
                                    (m) =>
                                        evaluate(m, thresholds) !==
                                        evaluate(m, DEFAULT_THRESHOLDS)
                                )
                                .slice(0, 12)
                                .map((m) => (
                                    <Typography
                                        key={m.id}
                                        variant='body2'
                                        sx={{
                                            fontVariantNumeric: 'tabular-nums',
                                        }}
                                    >
                                        Schärfe {m.sharpness?.toFixed(0) ?? '–'}{' '}
                                        · Helligkeit{' '}
                                        {m.brightness?.toFixed(0) ?? '–'} ·{' '}
                                        {REASON_LABEL[
                                            evaluate(m, thresholds)
                                        ] ?? 'Angenommen'}
                                    </Typography>
                                ))}
                            {measurements.filter(
                                (m) =>
                                    evaluate(m, thresholds) !==
                                    evaluate(m, DEFAULT_THRESHOLDS)
                            ).length === 0 && (
                                <Typography
                                    variant='body2'
                                    color='text.secondary'
                                >
                                    Keine — die Änderung würde nichts bewirken.
                                </Typography>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Stack>

            <PhotoReview measurements={measurements} thresholds={thresholds} />
        </Box>
    );
};
